import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useRideDtoActions } from "@ryde/redux";
import {
  castErrorResult,
  clearRideMlPathIgnoreReason,
  createErrorResult,
  createSuccessResultVoid,
  errors,
  getRideBaseDate,
  getRideDismissedMlPathStationRouteId,
  IDeleteStationRoutePayload,
  IRideDto,
  IRideMetadata,
  IRideSaveRoutePayload,
  IRideSaveRouteResponse,
  Log,
  MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultInvalidExecutionResult,
  StationRouteApi,
  transformRideMetadataToStationRouteResult,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export const useRideSaveRouteActions = () => {
  const { t } = useTranslation();
  const { updateEditingRideMetadataRideDate } = useRideDtoActions();

  const saveRideRouteAction = useEvent(
    async (
      rideDto: Partial<IRideDto>,
    ): Promise<SimpleResult<IRideSaveRouteResponse>> => {
      const stationsInfo = rideDto?.metadata?.stationsInfo || [];

      if (
        !rideDto?.metadata ||
        !stationsInfo?.length ||
        stationsInfo.length < MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD
      ) {
        return createErrorResult(errors.stationRoute.noStationsInRoute);
      }

      const routeResult = transformRideMetadataToStationRouteResult(
        rideDto.metadata,
      );

      if (!routeResult?.success) {
        return castErrorResult(routeResult);
      }

      if (!routeResult?.data) {
        return simpleResultInvalidExecutionResult(t);
      }

      const saveRideRoutePayload: IRideSaveRoutePayload = {
        id:
          rideDto.metadata?.customStationRouteId ??
          getRideDismissedMlPathStationRouteId(rideDto.metadata) ??
          undefined,
        date: getRideBaseDate(rideDto),
        rideId: rideDto.id,
        direction: rideDto.direction,
        route: routeResult.data,
      };

      const saveRouteResult =
        await StationRouteApi.saveStationRoute(saveRideRoutePayload);

      if (
        !saveRouteResult ||
        (saveRouteResult.success && !saveRouteResult.data?.stationRouteId)
      ) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (!saveRouteResult.success) {
        return castErrorResult(saveRouteResult);
      }

      const { stationRouteId } = saveRouteResult.data as IRideSaveRouteResponse;

      const metadataCloned: IRideMetadata = clearRideMlPathIgnoreReason({
        ...(rideDto?.metadata || {}),
        customStationRouteId: stationRouteId,
        usedCachedRouteId: stationRouteId,
      });
      updateEditingRideMetadataRideDate({
        rideId: rideDto.id,
        updatedMetadata: metadataCloned,
      });

      return saveRouteResult;
    },
  );
  const deleteRideRouteAction = useEvent(
    async (
      rideDto: Partial<IRideDto>,
      formStationRouteId?: number,
    ): Promise<SimpleResult<void>> => {
      const { customStationRouteId: metadataStationRouteId } =
        rideDto?.metadata || {};
      const stationRouteIdToDelete =
        formStationRouteId ?? metadataStationRouteId;

      const metadataCloned: IRideMetadata = clearRideMlPathIgnoreReason({
        ...(rideDto?.metadata || {}),
        customStationRouteId: null,
        usedCachedRouteId: null,
      });
      updateEditingRideMetadataRideDate({
        rideId: rideDto.id,
        updatedMetadata: metadataCloned,
      });

      if (!stationRouteIdToDelete) {
        return createSuccessResultVoid();
      }

      const deleteStationRoutePayload: IDeleteStationRoutePayload = {
        stationRouteId: stationRouteIdToDelete,
        rideId: rideDto.id,
      };

      const deleteRouteResult = await StationRouteApi.deleteStationRoute(
        deleteStationRoutePayload,
      );

      if (!deleteRouteResult) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (!deleteRouteResult.success) {
        return castErrorResult(deleteRouteResult);
      }

      return deleteRouteResult;
    },
  );

  const checkIfStationRouteExist = useEvent(
    async (stationRouteId: number): Promise<boolean> => {
      if (!stationRouteId) {
        Log.warnAndToast(t(errors.common.invalidId));
        return false;
      }

      const checkRouteResult = await StationRouteApi.checkStationRouteExists({
        stationRouteId,
      });

      if (!checkRouteResult) {
        warnAndToastSimpleResult(simpleResultErrorEmptyResponse(t), t);
        return false;
      }

      if (!checkRouteResult.success) {
        warnAndToastSimpleResult(checkRouteResult, t);
        return false;
      }

      if (checkRouteResult.success && checkRouteResult.data) {
        return true;
      } else {
        return false;
      }
    },
  );

  const checkIfStationRoutePaused = useEvent(
    async (stationRouteId: number, rideId: number): Promise<boolean> => {
      if (!stationRouteId) {
        Log.warnAndToast(t(errors.common.invalidId));
        return false;
      }

      const checkRouteResult = await StationRouteApi.checkStationRoutePaused({
        stationRouteId,
        rideId,
      });

      if (!checkRouteResult) {
        warnAndToastSimpleResult(simpleResultErrorEmptyResponse(t), t);
        return false;
      }

      if (!checkRouteResult.success) {
        warnAndToastSimpleResult(checkRouteResult, t);
        return false;
      }

      if (checkRouteResult.success && checkRouteResult.data) {
        return true;
      } else {
        return false;
      }
    },
  );

  return {
    saveRideRouteAction,
    deleteRideRouteAction,
    checkIfStationRouteExist,
    checkIfStationRoutePaused,
  };
};
